import React, { useState } from 'react';
import { Box, Button, Collapse, Link, Stack, Typography } from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import CodeField from './CodeField';
import { TOTP, otpauthUri, formatSetupKey } from '../totp';

// Pairing screen for an authenticator app: QR code, "can't scan" setup key,
// and the confirmation code field. Used for first-time setup and for
// switching apps, so both look and behave the same.
const TotpEnrollment = ({ issuer, account, secret, code, onCodeChange }) => {
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);
  const uri = otpauthUri({ issuer, account, secret });

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      setShowKey(true);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        1. Scan this QR code with your authenticator app
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        In 1Password, open the login for {issuer}, choose <strong>Edit → One-Time Password</strong> and
        scan it (or use the QR scanner in the 1Password app). Google Authenticator, Authy and others work too.
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
        {/* Always dark-on-white with a quiet zone, so it scans in dark mode too. */}
        <Box sx={{ p: 1.5, bgcolor: '#fff', borderRadius: 2, lineHeight: 0 }} data-testid="totp-qr">
          <QRCodeSVG value={uri} size={184} level="M" title={`QR code for ${issuer}`} />
        </Box>
      </Box>
      <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 1 }}>
        <Button size="small" onClick={() => setShowKey((v) => !v)} aria-expanded={showKey}>
          {showKey ? 'Hide setup key' : 'Can’t scan it?'}
        </Button>
        <Button size="small" component={Link} href={uri}>Open in app on this device</Button>
      </Stack>
      <Collapse in={showKey}>
        <Box sx={{ p: 1.5, mb: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
          <Typography variant="body2" color="text.secondary">Enter this setup key in your app:</Typography>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ my: 0.5 }}>
            <Typography
              data-testid="totp-setup-key"
              sx={{ fontFamily: 'monospace', fontSize: '1rem', wordBreak: 'break-all', flex: 1 }}
            >
              {formatSetupKey(secret)}
            </Typography>
            <Button size="small" variant="outlined" onClick={copyKey}>{copied ? 'Copied' : 'Copy'}</Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" component="div">
            Account: {account} · Time-based · {TOTP.digits} digits · every {TOTP.period} seconds · {TOTP.algorithm}
          </Typography>
        </Box>
      </Collapse>
      <Typography variant="subtitle2" sx={{ mt: 2 }}>
        2. Enter the 6-digit code your app now shows
      </Typography>
      <CodeField value={code} onChange={onCodeChange} id="totp-enrollment-code" />
    </Box>
  );
};

export default TotpEnrollment;
